'use client';

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Button } from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useModal } from "@/contexts/ModalContext";

export const HOTEL_FEATURES = {
  generalFeatures: [
    { label: 'Wifi' },
    { label: 'Açık Havuz' },
    { label: 'Otopark' },
    { label: 'Engelli Dostu' },
    { label: 'Asansör' },
    { label: 'Çatı Bahçesi' }
  ],
  roomFeatures: [
    { label: 'Teras' },
    { label: 'Oda Servisi', paid: true }
  ],
  spaWellness: [
    { label: 'Spa Küveti' },
    { label: 'Türk Hamamı' },
    { label: 'Sauna' },
    { label: 'Buhar Odası' }
  ],
  beach: [
    { label: 'Şezlong' },
    { label: 'Şemsiye' },
    { label: 'Özel Plaj' },
    { label: 'Plaj Havlusu' }
  ],
  business: [
    { label: 'İş Merkezi' },
    { label: 'Toplantı Odası', paid: true }
  ],
  restaurant: [
    { label: 'Bar' },
    { label: 'Kahvaltı' },
    { label: 'Kafe' },
    { label: 'Ziyafet Salonu' },
    { label: 'Restoran' },
    { label: 'Snack Bar' }
  ],
  child: [
    { label: 'Çocuk Bakıcısı', paid: true },
    { label: 'Çocuk Havuzu' },
    { label: 'Bebek Arabası' },
    { label: 'Çocuk Büfesi' },
    { label: 'Çocuk Kaydırağı' }
  ],
  sport: [
    { label: 'Dalış Merkezi' },
    { label: 'Çocuk Kulübü' },
    { label: 'Spor Salonu' },
    { label: 'Bisiklet' },
    { label: 'Çocuk Oyunları' },
    { label: 'Pilates' }
  ],
}

export const HOTEL_FEATURE_GROUP_ICONS = {
  generalFeatures: byPrefixAndName.fak["entertainment"],
  roomFeatures: byPrefixAndName.fak["hotel-door"],
  spaWellness: byPrefixAndName.far["spa"],
  beach: byPrefixAndName.far["umbrella-beach"],
  business: byPrefixAndName.far["briefcase"],
  restaurant: byPrefixAndName.far["fork-knife"],
  child: byPrefixAndName.far["child-reaching"],
  sport: byPrefixAndName.far["dumbbell"],
}

// CategoryId'ye göre feature group mapping
const CATEGORY_TO_GROUP = {
  1: 'generalFeatures', // Genel Özellikler
  2: 'restaurant', // Yeme & İçme
  3: 'sport', // Spor & Eğlence
  4: 'child', // Çocuk & Bebek
  7: 'beach', // Plaj
  8: 'roomFeatures', // Oda Özellikleri
};

const INITIAL_ITEMS_COUNT = 6;
const ITEMS_INCREMENT = 6;

const HotelFeatures = ({ hotelData }) => {
  const t = useTranslationDetector('hotel');
  const isMd = useMediaQuery("(min-width: 1024px)");
  const isMobile = useIsMobile();
  const allFeaturesModal = useModal("hotel-features-modal");

  // Her feature group için gösterilecek item sayısını tutan state
  const [visibleItemsCount, setVisibleItemsCount] = useState({});

  // API'den gelen HotelAmenities'i kategorilere göre grupla
  const groupedFeatures = useMemo(() => {
    const amenities = hotelData?.HotelAmenities || [];

    if (amenities.length === 0) {
      return HOTEL_FEATURES;
    }

    const grouped = {};

    amenities.forEach((amenity) => {
      const categoryId = amenity.CategoryId || 0;
      const groupKey = CATEGORY_TO_GROUP[categoryId] || 'generalFeatures';

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }

      grouped[groupKey].push({
        label: amenity.AmenityName || '',
        paid: amenity.Paid || false,
      });
    });

    // Eğer hiç feature yoksa default'ları kullan
    if (Object.keys(grouped).length === 0) {
      return HOTEL_FEATURES;
    }

    return grouped;
  }, [hotelData]);

  // Her feature group için toggle handler
  const handleToggleFeatures = (featureGroup) => {
    setVisibleItemsCount((prev) => {
      const currentCount = prev[featureGroup] || INITIAL_ITEMS_COUNT;
      const totalCount = groupedFeatures[featureGroup]?.length || 0;

      // Eğer tüm itemler gösteriliyorsa, 6'ya döndür
      if (currentCount >= totalCount) {
        return { ...prev, [featureGroup]: INITIAL_ITEMS_COUNT };
      }

      // Değilse, 6 item daha ekle
      const newCount = Math.min(currentCount + ITEMS_INCREMENT, totalCount);
      return { ...prev, [featureGroup]: newCount };
    });
  };

  const renderFeaturesTable = useMemo(() => {
    const columns = isMd ? 4 : 3;
    const table = [];

    const allKeys = Object.keys(groupedFeatures);
    const keys = isMobile ? allKeys.slice(0, 2) : allKeys;

    // kolonlara sırayla dağıt (0,1,2,0,1,2...)
    keys.forEach((key, i) => {
      const colIndex = i % columns;
      if (!table[colIndex]) table[colIndex] = [];
      table[colIndex].push(key);
    });

    return (
      <div className="flex flex-col md:flex-row gap-5 md:gap-12 justify-between flex-wrap min-w-0">
        {table.map((column, idx) => (
          <div className="flex flex-col gap-5 md:gap-12 flex-1 min-w-0 md:min-w-[250px]" key={idx}>
            {column.map((featureGroup) => {
              const allFeatures = groupedFeatures[featureGroup] || [];
              const visibleCount = visibleItemsCount[featureGroup] || INITIAL_ITEMS_COUNT;
              const visibleFeatures = allFeatures.slice(0, visibleCount);
              const totalCount = allFeatures.length;
              const isAllVisible = visibleCount >= totalCount;
              const icon = HOTEL_FEATURE_GROUP_ICONS[featureGroup] || HOTEL_FEATURE_GROUP_ICONS.generalFeatures;

              return (
                <div
                  key={`featureGroup_${featureGroup}`}
                  className="flex flex-col gap-4"
                >
                  <div className="flex flex-row items-center gap-2 text-sm md:text-base font-semibold">
                    <FontAwesomeIcon
                      icon={icon}
                      className="h-6! w-6! sm:h-7! sm:w-7!"
                    />
                    {t(`feature_${featureGroup}`) || featureGroup}
                  </div>

                  <ul className="flex flex-col gap-3">
                    {visibleFeatures.map((featureItem, i) => (
                      <li
                        key={`featureItem_${i}`}
                        className="flex flex-row items-center gap-2"
                      >
                        <FontAwesomeIcon
                          icon={byPrefixAndName.far["check"]}
                          className="h-4! w-4!"
                        />
                        <span className="text-sm md:text-base">
                          {featureItem.label}
                        </span>
                        {featureItem.paid && (
                          <span className="py-1 px-2 text-sm bg-[var(--color-background-global)] rounded-md">
                            {t('featurePaid')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>

                  {totalCount > INITIAL_ITEMS_COUNT && (
                    <button
                      onClick={() => handleToggleFeatures(featureGroup)}
                      className="flex flex-row gap-1 items-center text-sm md:text-base font-medium text-primary cursor-pointer"
                    >
                      {isAllVisible
        ? t("showLessButton") || t("showLess")
        : t("showMoreButton") || t("showMore")
                      }
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["chevron-down"]}
                        className={`text-sm transition-transform duration-200 ${isAllVisible ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }, [groupedFeatures, isMd, isMobile, t, visibleItemsCount]);

  // Modal içinde tüm otel özellikleri (tüm gruplar, tüm itemler)
  const renderModalFeatures = useMemo(() => {
    const allKeys = Object.keys(groupedFeatures);
    return (
      <div className="flex flex-col gap-6">
        {allKeys.map((featureGroup) => {
          const allFeatures = groupedFeatures[featureGroup] || [];
          const icon = HOTEL_FEATURE_GROUP_ICONS[featureGroup] || HOTEL_FEATURE_GROUP_ICONS.generalFeatures;
          return (
            <div
              key={`modal_featureGroup_${featureGroup}`}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-row items-center gap-2 text-sm md:text-base font-semibold">
                <FontAwesomeIcon icon={icon} className="h-7 w-7" />
                {t(`feature_${featureGroup}`) || featureGroup}
              </div>
              <ul className="flex flex-col gap-3">
                {allFeatures.map((featureItem, i) => (
                  <li
                    key={`modal_featureItem_${i}`}
                    className="flex flex-row items-center gap-2"
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["check"]}
                      className="h-5 w-5"
                    />
                    <span className="text-sm md:text-base">{featureItem.label}</span>
                    {featureItem.paid && (
                      <span className="py-1 px-2 text-sm bg-[var(--color-background-global)] rounded-md">
                        {t('featurePaid')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }, [groupedFeatures, t]);

  return (
    <>
      <div className="text-xl sm:text-[23px] md:text-[29px]! font-semibold mb-6 lg:mb-12">
        {t('hotelFeatures')}
      </div>
      <div className="flex flex-col gap-4 sm:gap-12">


        <div className="flex flex-col gap-4 sm:gap-6">
          {renderFeaturesTable}
        </div>

        <Button
          className="md:hidden"
          variant="outline"
          color="primary"
          onClick={() =>
            allFeaturesModal.open({
              title: t('hotelFeatures'),
              size: 'xl',
              isBottomSheet: true,
            })
          }
        >
          {t('allHotelFeatures')}
        </Button>

        <Modal id="hotel-features-modal">
          {renderModalFeatures}
        </Modal>
      </div>
    </>
  )
};

export default HotelFeatures;